import type { Editor } from "@repo/fika-blog/src/tiptap/core"
import type { NodeSelection } from "@repo/fika-blog/src/tiptap/pm/state"
import { createMemo } from "solid-js"
import { createEditorTransaction, useEditorIsActive } from "solid-tiptap"
import { Mini } from "@/designSystem/components/mini/mini"
import { Tooltip } from "@/designSystem/components/tooltip"
import {
  ParentButton,
  TransformButton,
} from "@/designSystem/patterns/editor-menu"

type Props = {
  editor: Editor
}
export function HeadingMenu(props: Props) {
  const selection = createMemo(
    createEditorTransaction(
      () => props.editor,
      editor => editor.state.selection as NodeSelection,
    ),
  )

  const isH1 = match("heading", { level: 1 })
  const isH2 = match("heading", { level: 2 })
  const isH3 = match("heading", { level: 3 })
  const isAlignLeft = match(undefined, { textAlign: "left" })
  const isAlignCenter = match(undefined, { textAlign: "center" })
  const isAlignRight = match(undefined, { textAlign: "right" })
  const isAlignJustify = match(undefined, { textAlign: "justify" })

  const textValue = createMemo(() => {
    if (isH1()) return "heading1"
    if (isH2()) return "heading2"
    if (isH3()) return "heading3"
    return null
  })

  const textAlignValue = createMemo(() => {
    if (isAlignLeft()) return "left"
    if (isAlignCenter()) return "center"
    if (isAlignRight()) return "right"
    if (isAlignJustify()) return "justify"
    return null
  })

  function match(type?: string, attrs?: Record<string, any>) {
    if (type) {
      return useEditorIsActive(
        () => props.editor,
        () => type,
        attrs,
      )
    }

    if (attrs) {
      return useEditorIsActive(() => props.editor, attrs)
    }

    throw new Error("No match")
  }

  function textOnChange(value: string | null) {
    switch (value) {
      case "heading1":
        return props.editor.chain().focus().setHeading({ level: 1 }).run()
      case "heading2":
        return props.editor.chain().focus().setHeading({ level: 2 }).run()
      case "heading3":
        return props.editor.chain().focus().setHeading({ level: 3 }).run()
    }
  }

  function textAlignOnChange(value: string | null) {
    if (!value) return
    return props.editor.chain().focus().setTextAlign(value).run()
  }

  const icons = {
    heading1: "heading-1",
    heading2: "heading-2",
    heading3: "heading-3",
  } as const

  return (
    <Mini.Menu>
      <Mini.Section>
        <ParentButton editor={props.editor} />
        <TransformButton editor={props.editor} selection={selection} />
      </Mini.Section>

      <Mini.Section>
        <Tooltip label="text style">
          <Mini.Select
            items={[
              {
                value: "heading3",
                label: "heading 3",
                iconName: icons.heading3,
              },
              {
                value: "heading2",
                label: "heading 2",
                iconName: icons.heading2,
              },
              {
                value: "heading1",
                label: "heading 1",
                iconName: icons.heading1,
              },
            ]}
            value={textValue() ?? "text"}
            hideValue
            onChange={textOnChange}
          />
        </Tooltip>
        <Tooltip label="text alignment">
          <Mini.Select
            items={[
              { value: "left", label: "left", iconName: "align-left" },
              { value: "center", label: "center", iconName: "align-center" },
              { value: "justify", label: "justify", iconName: "align-justify" },
              { value: "right", label: "right", iconName: "align-right" },
            ]}
            value={textAlignValue() ?? "justify"}
            hideValue
            onChange={textAlignOnChange}
          />
        </Tooltip>
      </Mini.Section>
    </Mini.Menu>
  )
}
